import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';

import { cadsRoleButtonKeydown, cadsRoleButtonKeyup } from '../../a11y/cads-role-button-keyboard';
import { CadsIconViewBitComponent } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.component';
import type { CadsIconViewBitConfig } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.config';
import { CadsCardViewConfig } from './cads-card-view.config';

/**
 * **Container only:** outer card chrome—optional section-style title, surface, border, shadow, optional tappable affordance,
 * and trailing chevron. **Inner row or body UI is not part of this component;** project content from other CADS components
 * (or the host app). The surface’s inner row applies horizontal and vertical padding (`--cads-space-md-sm`) so projected
 * rows (e.g. {@link CadsAccountRowComponent}) do not need their own card inset. Colored surfaces use CADS semantic tokens
 * (pastel background, deep-derived border/shadow/icon at defined opacities). Options are supplied via the `config` input
 * ({@link CadsCardViewConfig}; no i18n inside CADS v1).
 */
@Component({
    selector: 'cads-card-view',
    imports: [NgStyle, CadsIconViewBitComponent],
    templateUrl: './cads-card-view.component.html',
    styleUrl: './cads-card-view.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CadsCardViewComponent {
  /** Card options (title, color, tappability, trailing icon). Defaults apply when properties are omitted. */
  readonly config = input<CadsCardViewConfig>({});

  /** Fired when the card is tappable and the user activates it (click, Enter on keydown, or Space on keyup). */
  readonly tapped = output<void>();

  /**
   * Resolved config with defaults applied (`tappable` false, `disabled` false, `showTrailingIcon` true, `trailingIcon` `chevron_right`,
   * `trailingIconSize` `medium`).
   */
  readonly effectiveConfig = computed(() => {
    const c = this.config();
    return {
      title: c.title,
      color: c.color,
      tappable: c.tappable ?? false,
      disabled: c.disabled ?? false,
      trailingIcon: c.trailingIcon ?? 'chevron_right',
      showTrailingIcon: c.showTrailingIcon ?? true,
      trailingIconSize: c.trailingIconSize ?? 'medium',
    };
  });

  /** **True** when the card accepts pointer/keyboard activation. */
  readonly isInteractive = computed(() => {
    const e = this.effectiveConfig();
    return e.tappable && !e.disabled;
  });

  /** CSS variables forwarded to the surface for colored tokens. */
  readonly surfaceCssVars = computed<Record<string, string>>(() => {
    const key = this.effectiveConfig().color;
    if (!key) {
      return {} as Record<string, string>;
    }
    return {
      '--cads-card-view-pastel': `var(--cads-color-${key}-pastel)`,
      '--cads-card-view-deep': `var(--cads-color-${key}-deep)`,
    };
  });

  readonly showTrailingChevron = computed(() => {
    const e = this.effectiveConfig();
    return this.isInteractive() && e.showTrailingIcon && this._isChevronIcon(e.trailingIcon);
  });

  /** Trailing chevron as {@link CadsIconViewBitComponent} (`shape: icon`) using the shared CADS sprite. */
  readonly trailingChevronBitConfig = computed((): CadsIconViewBitConfig => {
    const e = this.effectiveConfig();
    return {
      icon: this._trailingGlyphToSymbolId(e.trailingIcon),
      shape: 'icon',
      size: e.trailingIconSize,
      collapsed: true,
      color: e.color ? `var(--cads-color-${e.color}-deep)` : 'var(--cads-color-mid-gray)',
    };
  });

  protected _onActivate(): void {
    if (!this.isInteractive()) {
      return;
    }
    this.tapped.emit();
  }

  protected _onKeydown(event: KeyboardEvent): void {
    cadsRoleButtonKeydown(event, this.isInteractive(), () => this._onActivate());
  }

  protected _onKeyup(event: KeyboardEvent): void {
    cadsRoleButtonKeyup(event, this.isInteractive(), () => this._onActivate());
  }

  private _isChevronIcon(name: string): boolean {
    return name === 'chevron_right' || name === 'chevron-right';
  }

  private _trailingGlyphToSymbolId(name: string): string {
    const n = name.trim();
    if (n === 'chevron_right' || n === 'chevron-right') {
      return 'chevron_right';
    }
    return n.replace(/-/g, '_');
  }
}
